import { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { MapPin, Check, Loader2 } from "lucide-react";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_PUBLIC_KEY;

// Module-level promise so we only load the Google Maps JS SDK once across the app.
// Uses the new functional API (setOptions + importLibrary) from
// @googlemaps/js-api-loader v2+ (the deprecated `Loader` class crashes at runtime).
let _mapsLoadPromise = null;
function loadMaps() {
  if (_mapsLoadPromise) return _mapsLoadPromise;
  if (!API_KEY) {
    _mapsLoadPromise = Promise.reject(new Error("REACT_APP_GOOGLE_MAPS_PUBLIC_KEY missing"));
    return _mapsLoadPromise;
  }
  setOptions({ key: API_KEY, v: "weekly" });
  _mapsLoadPromise = importLibrary("places");
  return _mapsLoadPromise;
}

/**
 * Google Places Autocomplete wrapper around a plain <input>.
 * Props:
 *  - value: current address string (controlled)
 *  - onChange(value): called on every keystroke
 *  - onVerified({formatted_address, lat, lng, place_id, components})
 *      fired when the user picks a Google-suggested prediction
 *  - onReset(): fired when the user clears or edits a previously verified address
 *  - required, placeholder, className, id, dataTestId — pass-through to the input
 *
 *  If the Google Maps key is not configured, the component gracefully falls
 *  back to a plain uncontrolled text input — no errors or empty states.
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onVerified,
  onReset,
  required = false,
  placeholder = "123 Main St, City, State",
  className = "input-clean w-full",
  id,
  dataTestId = "address-autocomplete-input",
  verified = false,
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Block Radix Dialog from interpreting clicks on the Google `.pac-container`
    // (which is appended to <body>, OUTSIDE the dialog tree) as an "outside
    // click" and auto-closing the modal. We swallow mousedown/pointerdown on
    // any .pac-container BEFORE Radix's dismissable layer hears it. Capture-
    // phase so it fires before Radix's document-level listener. Idempotent —
    // attached once per AddressAutocomplete mount; harmless if no dialog open.
    const stopRadixOutsideClose = (e) => {
      const t = e.target;
      if (t && t.closest && t.closest(".pac-container")) {
        e.stopPropagation();
      }
    };
    document.addEventListener("mousedown", stopRadixOutsideClose, true);
    document.addEventListener("pointerdown", stopRadixOutsideClose, true);
    document.addEventListener("touchstart", stopRadixOutsideClose, true);

    loadMaps()
      .then(() => {
        if (cancelled) return;
        if (!inputRef.current || !window.google?.maps?.places) return;
        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ["address"],
          fields: ["formatted_address", "geometry", "address_components", "place_id"],
        });
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          if (!place || !place.geometry) return;
          const lat = place.geometry.location?.lat?.();
          const lng = place.geometry.location?.lng?.();
          const comps = {};
          for (const c of place.address_components || []) {
            for (const type of c.types) {
              comps[type] = c.long_name;
              comps[`${type}_short`] = c.short_name;
            }
          }
          const fa = place.formatted_address || inputRef.current.value;
          if (onChange) onChange(fa);
          if (onVerified) onVerified({
            formatted_address: fa,
            lat, lng,
            place_id: place.place_id,
            components: comps,
          });
        });        autocompleteRef.current = ac;
        setReady(true);
      })
      .catch((err) => {
        console.warn("Google Places SDK failed to load — falling back to plain input", err);
        setLoadError(true);
      });
    return () => {
      cancelled = true;
      document.removeEventListener("mousedown", stopRadixOutsideClose, true);
      document.removeEventListener("pointerdown", stopRadixOutsideClose, true);
      document.removeEventListener("touchstart", stopRadixOutsideClose, true);
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const v = e.target.value;
    if (onChange) onChange(v);
    // If user modifies a previously-verified address, it's no longer verified.
    if (verified && onReset) onReset();
  };

  return (
    <div className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
      <input
        ref={inputRef}
        id={id}
        type="text"
        required={required}
        placeholder={placeholder}
        value={value || ""}
        onChange={handleChange}
        className={`${className} pl-9 pr-20`}
        data-testid={dataTestId}
        autoComplete="off"
      />
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest pointer-events-none">
        {verified ? (
          <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="address-verified-badge">
            <Check className="w-3 h-3" /> Verified
          </span>
        ) : loadError ? (
          <span className="text-slate-400">Manual</span>
        ) : !ready ? (
          <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
        ) : (
          <span className="text-slate-400">Start typing…</span>
        )}
      </div>
    </div>
  );
}
